/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */


import React, { useEffect } from 'react';

import { IMfrmCompleteYourBedData } from './mfrm-complete-your-bed.data';
import { IMfrmCompleteYourBedProps } from './mfrm-complete-your-bed.props.autogenerated';
import { getActivePricesAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductSearchCriteria, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getBVAttributeValues } from '../../Utilities/get-bv-attributes';
import { getByIdsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import fetchCompleteYourBedDataDataAction, { FetchCompleteYourBedDataInput, IDataRow } from '../../actions/fetch-complete-your-bed.action';
import { CompleteYourBedPillowInput } from '../../actions/complete-your-bed-pillow.action';
import { generateImageUrl } from '../../data-actions/utilities/utils';
import { CompleteyourBedProtectorInput } from '../../actions/complete-your-bed-protector.action';
import { getAttributeByName } from '../../Utilities/getAttributeUtils';
import { _getProductType } from '../../themes/mattressfirm/views/components/delivery-message/deliverymessage.component';
import { _smallParcelProductATPBooleanStatus } from '../../Utilities/mfrm-buybox-utils';

export interface IMfrmCompleteYourBedViewProps extends IMfrmCompleteYourBedProps<IMfrmCompleteYourBedData> {
    product: SimpleProduct;
    items: MergedProduct[];
    protectorData: MergedProduct[];
    onUnselect(arg0: SimpleProduct, arg1: number): void;
    onSelect(arg0: SimpleProduct, arg1: number, arg2: boolean | undefined): void;
    onUnselectProtector(arg0: SimpleProduct, arg1: number): void;
    onSelectProtector(arg0: SimpleProduct, arg1: number, arg2: boolean | undefined): void;
    matressProtectorData: any;
    protectorSelected: boolean;
    pillowSelected: boolean;
}
export interface MergedProduct extends SimpleProduct {
    productName?: string;
    ItemId?: string;
    CustomerContextualPrice: number | undefined;
    DiscountAmount: number | undefined;
    TotalRatings: number | undefined;
    AverageRating: number | undefined;
    brand?: string;
    PrimaryImageUrl?: string;
    isSelected: boolean;
    inventoryStatus: { outOfStock: boolean | undefined, productType: string | undefined };
}
/**
 *
 * MfrmCompleteYourBed component
 * @extends {React.PureComponent<IMfrmCompleteYourBedProps<IMfrmCompleteYourBedData>>}
 */

export const MfrmCompleteYourBed: React.FC<IMfrmCompleteYourBedProps<IMfrmCompleteYourBedData>> = props => {
    // let smallParcelData: any[] = [];
    const [items, setItems] = React.useState<MergedProduct[]>([]);
    const [protectorData, setProtectorData] = React.useState<MergedProduct[]>([]);
    // const [matressProtectorData, setMatressProtectorData] = React.useState<any>(null);
    const [protectorSelected, setProtectorSelected] = React.useState(false);
    const [pillowSelected, setPillowSelected] = React.useState(false);
    const [database, setDatabase] = React.useState<any>(null);
    const {
        data: {
            product: { result: productResult },
            productCompareBuyboxStateSharing: { result: buyboxState }
        }
    } = props;
    const getVariantIds = (RecordId: any) => {
        const databaseRow: IDataRow = database.find((item: any /* this is obvi */) => item.mattressVariantReqId === `${RecordId}`);
        setItems([]);
        setProtectorData([]);
        props.context.actionContext.update(new CompleteYourBedPillowInput(null), { pillow: null });
        props.context.actionContext.update(new CompleteyourBedProtectorInput(null), { protector: null });
        const buyAll3Array = databaseRow?.buyAllVariantRecIds;
        if (buyAll3Array && buyAll3Array.length > 0) {
            buyAll3Array[0] && buyAll3Array && getProductsFromProductId(Number(buyAll3Array[0]), "pillow");
            if (buyAll3Array.length > 1) {
                buyAll3Array[1] && buyAll3Array && getProductsFromProductId(Number(buyAll3Array[1]), "protector");
            }
        }
    };
    const getProductsFromProductId = async (RecordId: number, type: string) => {

        const products = await getByIdsAsync(
            { callerContext: props.context.actionContext },
            props.context.request.apiSettings.channelId,
            [RecordId]
        );
        const inventoryStatus = await getInventoryStatus(RecordId, products[0]?.ItemId?.toString(), undefined, undefined);
        const productPrices = await getActivePricesAsync(
            { callerContext: props.context.actionContext },
            {
                ChannelId: props.context.actionContext.requestContext.apiSettings.channelId,
                CatalogId: props.context.actionContext.requestContext.apiSettings.catalogId
            },
            [RecordId],
            new Date(),
            null,
            [],
            true
        );
        let productSearchResults: ProductSearchResult[] = [];
        if (products.length) {
            const productSearchCriteria: ProductSearchCriteria = {
                Ids: [...new Set(products.map(item => item.MasterProductId ?? 0))],
                Context: {
                    ChannelId: +props.context.actionContext.requestContext.apiSettings.channelId,
                    CatalogId: +props.context.actionContext.requestContext.apiSettings.catalogId
                },
                IncludeAttributes: true,
                SearchCondition: '*'
            };
            productSearchResults = await searchByCriteriaAsync({ callerContext: props.context.actionContext }, productSearchCriteria);
        }
        const _parseValue = (productRating?: string | number): number => {
            const value = +(productRating || 0);
            return Math.round(value * 10) / 10;
        };
        const MergedProducts: any[] = products.map((product, i) => {
            const productImageUrl = product.PrimaryImageUrl;
            return {
                ...product,
                productName: product.Name,
                ItemId: product.ItemId,
                CustomerContextualPrice: productPrices[i].CustomerContextualPrice,
                DiscountAmount: productPrices[i].DiscountAmount,
                AverageRating:
                    getBVAttributeValues(
                        productSearchResults.find(item => item.MasterProductId === product.MasterProductId)?.AttributeValues,
                        'BVAverageOverallRating'
                    ) ?? _parseValue(productSearchResults.find(item => item.MasterProductId === product.MasterProductId)?.AverageRating),
                TotalRatings:
                    getBVAttributeValues(
                        productSearchResults.find(item => item.MasterProductId === product.MasterProductId)?.AttributeValues,
                        'BVTotalReviewCount'
                    ) ?? _parseValue(productSearchResults.find(item => item.MasterProductId === product.MasterProductId)?.TotalRatings),
                brand: productSearchResults.find(item => item.MasterProductId === product.MasterProductId)?.AttributeValues?.find(attr => attr.KeyName?.trim() === 'VendorBrand')?.TextValue,
                PrimaryImageUrl: generateImageUrl(productImageUrl, props.context.request.apiSettings),
                inventoryStatus
            };
        });

        type === "pillow" ? setItems(MergedProducts) : setProtectorData(MergedProducts);
        type === "protector" && MergedProducts && protectorSelected && props.context.actionContext.update(new CompleteyourBedProtectorInput(MergedProducts[0]), { protector: MergedProducts[0], listPosition: 0 });
        type === "pillow" && MergedProducts && pillowSelected && props.context.actionContext.update(new CompleteYourBedPillowInput(MergedProducts[0]), { pillow: MergedProducts[0], listPosition: 0 });
    };

    const getInventoryStatus = async (variantId: number, itemId: string | undefined, productTypeArg: string | undefined, outOfStock: boolean | undefined, isZipCodeChnaged: boolean = false):
        Promise<{ productType: string, outOfStock: boolean | undefined }> => {
        if (!productTypeArg) {
            const attributeValues = await getAttributeValuesAsync(
                {
                    callerContext: props.context.actionContext,
                    queryResultSettings: {}
                },
                variantId,
                props.context.actionContext.requestContext.apiSettings.channelId,
                props.context.actionContext.requestContext.apiSettings.catalogId
            );
            productTypeArg = _getProductType(getAttributeByName(attributeValues, 'shippingInformation')?.TextValue).toLowerCase();
            outOfStock = getAttributeByName(attributeValues, 'Out of Stock')?.BooleanValue ?? false;
        }
        if (productTypeArg === 'small parcel' && (isZipCodeChnaged || !outOfStock)) {
            outOfStock = await _smallParcelProductATPBooleanStatus(variantId, itemId, props.context.app.config.weeksPDPforATPCall, props.context);
        }
        return { productType: productTypeArg, outOfStock };
    };

    const onSelectPillow = (product: SimpleProduct | null | undefined, index: any, outOfStock?: boolean | undefined) => {
        if (outOfStock) {
            return;
        }
        props.context.actionContext.update(new CompleteYourBedPillowInput(product), { pillow: product, listPosition: index });
        const clone = items.map((item, i) => {
            if (index === i) {
                item.isSelected = true;
            }

            return item;
        });
        setItems(clone);
        setPillowSelected(true);

    };

    const onSelectProtector = (product: SimpleProduct | null | undefined, index: any, outOfStock?: boolean | undefined) => {
        if (outOfStock) {
            return;
        }
        props.context.actionContext.update(new CompleteyourBedProtectorInput(product), { protector: product, listPosition: index });
        const clone = protectorData.map((item, i) => {
            if (index === i) {
                item.isSelected = true;
            }

            return item;
        });
        setProtectorData(clone);
        setProtectorSelected(true);

    };

    const onUnselectPillow = (product: SimpleProduct | null | undefined, index: any, outOfStock?: boolean | undefined) => {
        props.context.actionContext.update(new CompleteYourBedPillowInput(null), { pillow: null });
        const clone = items.map((item, i) => {
            if (index === i) {
                item.isSelected = false;
                if (outOfStock) {
                    item.inventoryStatus.outOfStock = outOfStock;
                }
            }

            return item;
        });
        setItems(clone);
        setPillowSelected(false);

    };
    const onUnselectProtector = (product: SimpleProduct | null | undefined, index: any, outOfStock?: boolean | undefined) => {
        props.context.actionContext.update(new CompleteyourBedProtectorInput(null), { protector: null });
        const clone = protectorData.map((item, i) => {
            if (index === i) {
                item.isSelected = false;
                if (outOfStock) {
                    item.inventoryStatus.outOfStock = outOfStock;
                }
            }

            return item;
        });
        setProtectorData(clone);
        setProtectorSelected(false);

    };

    const inventoryCheckZipCode = async () => {
        if (buyboxState?.zipCode) {
            if (items?.length > 0 && (items[0].inventoryStatus?.productType === 'small parcel')) {
                await getInventoryStatus(items[0]?.RecordId, items[0]?.ItemId?.toString(), items[0].inventoryStatus?.productType, items[0].inventoryStatus?.outOfStock, true).then((status: any) => {
                    setItems(prevState => {
                        const newItems = [...prevState];
                        newItems[0] = { ...newItems[0], inventoryStatus: { productType: newItems[0].inventoryStatus.productType, outOfStock: status?.outOfStock } };
                        return newItems;
                    });
                    if (status?.outOfStock && pillowSelected) {
                        onUnselectPillow(null, 0, status?.outOfStock);
                    }
                }).catch((err) => console.log(err));
            }
            if (protectorData?.length > 0 && (protectorData[0].inventoryStatus?.productType === 'small parcel')) {
                await getInventoryStatus(protectorData[0]?.RecordId, protectorData[0]?.ItemId?.toString(), protectorData[0].inventoryStatus?.productType, protectorData[0].inventoryStatus?.outOfStock, true).then((status: any) => {
                    setProtectorData(prevState => {
                        const newItems = [...prevState];
                        newItems[0] = { ...newItems[0], inventoryStatus: { productType: newItems[0].inventoryStatus.productType, outOfStock: status?.outOfStock } };
                        return newItems;
                    });
                    if (status?.outOfStock && protectorSelected) {
                        onUnselectProtector(null, 0, status?.outOfStock);
                    }
                }
                ).catch((err) => console.log(err));
            }
        }
    };
    useEffect(() => {
        const actionInput = new FetchCompleteYourBedDataInput(props.context.app.config.jsonSrc);
        fetchCompleteYourBedDataDataAction(actionInput, props.context.actionContext)
            .then(data => setDatabase(data))
            .catch(e => {
                console.error('Error fetching database', e);
            });
    }, []);
    useEffect(() => {
        void inventoryCheckZipCode();
    }, [buyboxState?.zipCode]);
    useEffect(() => {
        database && productResult?.RecordId && getVariantIds(productResult?.RecordId);
    }, [productResult?.RecordId, database !== null]);
    return props.renderView({
        ...props,
        items,
        onSelect: onSelectPillow,
        onSelectProtector: onSelectProtector,
        onUnselect: onUnselectPillow,
        onUnselectProtector: onUnselectProtector,
        protectorData: protectorData,
        protectorSelected: protectorSelected,
        pillowSelected: pillowSelected
    });
};

export default MfrmCompleteYourBed;


